"use client";

import React, { useState, useEffect } from "react";

interface AdvancedInputsProps {
    /** LTV ratio (%) */
    ltv: number;
    setLtv: (v: number) => void;

    /** Property‑tax rate (% of home value per year) */
    propertyTax: number;
    setPropertyTax: (v: number) => void;

    /** Home‑owners insurance – dollars per year */
    insurance: number;
    setInsurance: (v: number) => void;
}

const fmtCommas = (v: number | string) => v.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const AdvancedInputs: React.FC<AdvancedInputsProps> = ({
    ltv,
    setLtv,
    propertyTax,
    setPropertyTax,
    insurance,
    setInsurance,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    /* local formatted strings */
    const [propertyTaxInput, setPropertyTaxInput] = useState("0");
    const [insuranceInput, setInsuranceInput] = useState("0");
    const [ltvInput, setLtvInput] = useState("0");

    /* sync props → local inputs */
    useEffect(() => setLtvInput((ltv ?? 0).toString()), [ltv]);
    useEffect(() => setPropertyTaxInput((propertyTax ?? 0).toString()), [propertyTax]);
    useEffect(() => setInsuranceInput(fmtCommas(insurance ?? 0)), [insurance]);

    /* ────────────────────────────────────────────────────────────────── */

    return (
        <div className="border border-gray-300 overflow-hidden shadow 2xl:w-[900px]">
            {/* accordion header */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full text-left bg-[#202358] text-white px-4 py-3 flex items-center justify-between hover:bg-blue 2xl:h-[42px]"
            >
                <h5 className="text-lg font-bold">ADVANCED</h5>
                <span className="text-xl">{isOpen ? "−" : "+"}</span>
            </button>

            {/* accordion body */}
            <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                }`}
            >
                <div className="bg-white p-3 2xl:w-[898px] space-y-2">
                    {/* ── LTV ─────────────────────────────────────── */}
                    <div>
                        <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-center 2xl:gap-4">
                            <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                                <label htmlFor="ltv" className="w-32 font-medium text-black text-[15px] 2xl:w-[150px]">
                                    Loan‑to‑Value Ratio: %
                                </label>
                                <input
                                    id="ltv"
                                    type="number"
                                    className="border px-3 py-1 rounded w-36 bg-[#f6f9ff] text-[14px] 2xl:w-[122px]"
                                    value={ltvInput}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        if (/^\d*\.?\d*$/.test(raw) && Number(raw) <= 100) {
                                            setLtv(Number(raw));
                                            setLtvInput(raw);
                                        }
                                    }}
                                    onBlur={() => setLtvInput(ltv.toString())}
                                />
                            </div>

                            <input
                                type="range"
                                min={0}
                                max={100}
                                step={0.5}
                                value={ltv}
                                onChange={(e) => setLtv(Number(e.target.value))}
                                className="w-full 2xl:w-[506px] accent-blue"
                            />
                        </div>
                        <div className="flex justify-end text-xs text-black mt-1">
                            <div className="pb-10 flex justify-between w-full max-w-md 2xl:pb-0">
                                <span>0%</span>
                                <span>33%</span>
                                <span>66%</span>
                                <span>100%</span>
                            </div>
                        </div>
                    </div>

                    {/* ── PROPERTY TAX % ─────────────────────────── */}
                    <div>
                        <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-center 2xl:gap-4">
                            <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                                <label
                                    htmlFor="propertyTax"
                                    className="w-32 font-medium text-black text-[15px] 2xl:w-[125px]"
                                >
                                    Property Tax / Yr: %
                                </label>
                                <input
                                    id="propertyTax"
                                    type="number"
                                    className="border px-3 py-1 rounded w-36 bg-[#f6f9ff] text-[14px] 2xl:w-[122px]"
                                    value={propertyTaxInput}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        if (/^\d*\.?\d*$/.test(raw) && Number(raw) <= 5) {
                                            setPropertyTax(Number(raw));
                                            setPropertyTaxInput(raw);
                                        }
                                    }}
                                    onBlur={() => setPropertyTaxInput(propertyTax.toString())}
                                />
                            </div>

                            <input
                                type="range"
                                min={0}
                                max={5}
                                step={0.05}
                                value={propertyTax}
                                onChange={(e) => setPropertyTax(Number(e.target.value))}
                                className="w-full 2xl:w-[506px] accent-blue"
                            />
                        </div>
                        <div className="flex justify-end text-xs text-black mt-1">
                            <div className="pb-10 flex justify-between w-full max-w-md 2xl:pb-0">
                                <span>0%</span>
                                <span>1.7%</span>
                                <span>3.3%</span>
                                <span>5%</span>
                            </div>
                        </div>
                    </div>

                    {/* ── INSURANCE $ ─────────────────────────────── */}
                    <div>
                        <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-center 2xl:gap-4">
                            <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                                <label
                                    htmlFor="insurance"
                                    className="w-32 font-medium text-black text-[15px] 2xl:w-[106px]"
                                >
                                    Insurance / Yr: $
                                </label>
                                <input
                                    id="insurance"
                                    type="text"
                                    inputMode="numeric"
                                    className="border px-3 py-1 rounded w-36 bg-[#f6f9ff] text-[14px] 2xl:w-[122px]"
                                    value={insuranceInput}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace(/,/g, "");
                                        if (/^\d*$/.test(raw)) {
                                            setInsurance(Number(raw));
                                            setInsuranceInput(fmtCommas(raw));
                                        }
                                    }}
                                    onBlur={() => setInsuranceInput(fmtCommas(insurance))}
                                />
                            </div>

                            <input
                                type="range"
                                min={0}
                                max={1_000_000}
                                step={1_000}
                                value={insurance}
                                onChange={(e) => setInsurance(Number(e.target.value))}
                                className="w-full 2xl:w-[506px] accent-blue"
                            />
                        </div>
                        <div className="flex justify-end text-xs text-black mt-1">
                            <div className="pb-10 flex justify-between w-full max-w-md 2xl:pb-0">
                                <span>$0</span>
                                <span>$10K</span>
                                <span>$100K</span>
                                <span>$1M</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdvancedInputs;
